import React from "react";
import { useState, useEffect } from 'react';
import { KLAVIYO_PUBLIC_KEY, KLAVIYO_LIST_ID, DEFAULT_SEASON_ID } from '../../../server/services/custom-pages/constants';
import { fetchPlayerTrend, fetchTopPerformersBySeason } from '../../../server/services/custom-pages/statscore';
import { fetchPlayersByStatscoreIds, fetchStoriesByPlayerName } from '../../../server/services/custom-pages/quintype';
import { syncFromKlaviyo, store, subscribeEntityToKlaviyo } from '../../../server/services/custom-pages/auth';

export function usePlayerStats(initialStats, statscoreId, playerEntity, initialIsMember = false) {
    const [isMember, setIsMember] = useState(initialIsMember);
    const [isSyncing, setIsSyncing] = useState(!initialIsMember);
    const [isRevealing, setIsRevealing] = useState(false);
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [stats, setStats] = useState({
        ...initialStats,
        isLoading: false
    });

    useEffect(() => {
        const checkAndFetchMissingData = async () => {
            if (stats.trendData === null || stats.trendData.length === 0) {
                try {
                    const seasonId = playerEntity?.statscore_season_id || playerEntity?.season_id || DEFAULT_SEASON_ID;
                    const [trendData, topPerformers] = await Promise.all([
                        fetchPlayerTrend(statscoreId, playerEntity?.name),
                        fetchTopPerformersBySeason(seasonId, playerEntity?.position || '')
                    ]);

                    const benchmarkEntities = await fetchPlayersByStatscoreIds(topPerformers.map(p => p.id));
                    const benchmarks = topPerformers.map(p => {
                        const entity = benchmarkEntities.find(e =>
                            String(e.statscore_player_id || e.metadata?.statscore_player_id) === String(p.id)
                        );
                        return { id: p.id, name: p.name, rating: p.rating, value: entity?.base_value || 0 };
                    });

                    setStats(prev => ({
                        ...prev,
                        trendData,
                        benchmarks
                    }));
                } catch (err) {
                    console.error("Error fetching missing data client-side:", err);
                }
            }
        };

        syncFromKlaviyo().then(res => {
            if (res.isSignedIn && res.email) {
                setEmail(res.email);
                store.set("fi_was_signed_in", "true");
                
                // Trust local cookie first to avoid asynchronous Klaviyo indexing lag
                const hasLocalCookie = statscoreId 
                    ? document.cookie.includes(`klaviyo_followed_${statscoreId}=true`)
                    : false;

                const isFollowing = hasLocalCookie || !!(
                    statscoreId &&
                    res.followedEntities &&
                    res.followedEntities.some(e => String(e.id || e) === String(statscoreId))
                );

                if (isFollowing) {
                    setIsMember(true);
                    if (statscoreId) {
                        document.cookie = `klaviyo_followed_${statscoreId}=true; path=/; max-age=${60 * 60 * 24 * 365}`;
                    }
                    checkAndFetchMissingData();
                } else {
                    setIsMember(false);
                    if (statscoreId) {
                        document.cookie = `klaviyo_followed_${statscoreId}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
                    }
                    if (!hasLocalCookie) {
                        document.cookie = "klaviyo_member=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;";
                    }
                }
            } else {
                // Not signed in to site - rely on cookies
                const isMemberCookie = statscoreId 
                    ? document.cookie.includes(`klaviyo_followed_${statscoreId}=true`)
                    : false;
                if (isMemberCookie || initialIsMember) {
                    setIsMember(true);
                    checkAndFetchMissingData();
                } else {
                    setIsMember(false);
                }
            }
            setIsSyncing(false);
        }).catch(err => {
            console.error("[usePlayerStats] sync error:", err);
            setIsSyncing(false);
        });
    }, [statscoreId, playerEntity?.name, initialIsMember]);

    const handleUnlock = async (e) => {
        e.preventDefault();
        if (!email || !email.includes('@')) {
            setEmailError('Please enter a valid email address.');
            return;
        }

        setIsSubmitting(true);
        setEmailError("");
        store.set("user_email", email);

        if (statscoreId) {
            document.cookie = `klaviyo_followed_${statscoreId}=true; path=/; max-age=${60 * 60 * 24 * 365}`;
        }
        document.cookie = "klaviyo_member=true; path=/; max-age=" + (60 * 60 * 24 * 365);
        
        // Unblur instantly
        setIsMember(true);
        setIsSubmitting(false);

        const currentClubName = playerEntity?.club || 
            playerEntity?.current_club || 
            playerEntity?.current_club_name || 
            (playerEntity?.['current-club-entity'] && playerEntity['current-club-entity'][0]?.name) || 
            null;

        subscribeEntityToKlaviyo(email, {
            id: statscoreId,
            name: playerEntity?.name || "",
            name_for_digest: playerEntity?.name || "",
            entityType: 'player',
            currentClubName: currentClubName
        }).catch(err => {
            console.warn("[usePlayerStats] Klaviyo background subscription:", err);
        });

        try {
            
            if (statscoreId) {
                const seasonId = playerEntity?.statscore_season_id || playerEntity?.season_id || DEFAULT_SEASON_ID;
                
                // Fetch stats asynchronously in the background
                Promise.all([
                    fetchPlayerTrend(statscoreId, playerEntity?.name),
                    fetchTopPerformersBySeason(seasonId, playerEntity?.position || '')
                ]).then(async ([trendData, topPerformers]) => {
                    const benchmarkEntities = await fetchPlayersByStatscoreIds(topPerformers.map(p => p.id));
                    const benchmarks = topPerformers.map(p => {
                        const entity = benchmarkEntities.find(e =>
                            String(e.statscore_player_id || e.metadata?.statscore_player_id) === String(p.id)
                        );
                        return { id: p.id, name: p.name, rating: p.rating, value: entity?.base_value || 0 };
                    });

                    setStats(prev => ({
                        ...prev,
                        trendData,
                        benchmarks
                    }));
                }).catch(err => {
                    console.error("Error fetching background stats:", err);
                });
            }

        } catch (err) {
            console.error("Unlock Error:", err);
            setEmailError('Something went wrong. Please try again.');
            setIsSubmitting(false);
        }
    };

    return {
        isMember,
        isRevealing,
        email,
        setEmail,
        emailError,
        setEmailError,
        isSubmitting,
        handleUnlock,
        stats,
        setStats,
        isSyncing
    };
}
